import { useState } from 'react';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import CanonicalTag from '@/components/CanonicalTag';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Calendar, Home, DollarSign, Building, HelpCircle, Users, Wifi, Coffee, Monitor } from 'lucide-react';

const HowToUseADUAsCoWorkingOrSharedOffice = () => {
  const [showCalendly, setShowCalendly] = useState(false);

  const ctaButtons = [
    {
      icon: Calendar,
      text: "Book & Schedule",
      onClick: () => setShowCalendly(true)
    },
    {
      icon: Building,
      text: "What Fits My Lot?",
      onClick: () => window.open('/landing', '_self')
    },
    {
      icon: DollarSign,
      text: "Get Price & ROI Report",
      onClick: () => window.open('/adu-calculator', '_self')
    },
    {
      icon: Home,
      text: "Our Portfolio",
      onClick: () => window.open('/our-work', '_self')
    },
    {
      icon: HelpCircle,
      text: "Our FAQ",
      onClick: () => window.open('/faq', '_self')
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/how-to-use-adu-as-co-working-or-shared-office" />
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-20 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#E4683B' }}>
            How to Use Your ADU as a Shared Office or Backyard Co-Working Studio
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            Offer freelancers, consultants, or remote teams a safe, flexible backyard space with all the professionalism of a downtown office—without the overhead.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {ctaButtons.map((button, index) => (
              <Button
                key={index}
                onClick={button.onClick}
                className="flex items-center gap-2"
                style={{ backgroundColor: index === 0 ? '#E4683B' : 'transparent', borderColor: '#E4683B', color: 'white' }}
                variant={index === 0 ? "default" : "outline"}
              >
                <button.icon className="w-4 h-4" />
                {button.text}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Community Productivity Section */}
      <section className="py-16 px-4 bg-secondary/50">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>Build Community Productivity Right at Home</h2>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5" style={{ color: '#E4683B' }} />
                  <span className="text-white">Freelancers and consultants</span>
                </div>
                <div className="flex items-center gap-3">
                  <Monitor className="w-5 h-5" style={{ color: '#E4683B' }} />
                  <span className="text-white">Remote teams and startups</span>
                </div>
                <div className="flex items-center gap-3">
                  <Wifi className="w-5 h-5" style={{ color: '#E4683B' }} />
                  <span className="text-white">Digital nomads seeking community</span>
                </div>
                <div className="flex items-center gap-3">
                  <Coffee className="w-5 h-5" style={{ color: '#E4683B' }} />
                  <span className="text-white">Local professionals needing workspace</span>
                </div>
              </div>
            </div>
            <div className="bg-muted rounded-lg p-8">
              <img 
                src="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=800&h=400" 
                alt="Co-working Shared Office ADU" 
                className="w-full h-64 object-cover rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Space Layout Suggestions Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: '#E4683B' }}>Space Layout Suggestions</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Work Environment</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Open layout for 2–3 desks</li>
                <li>• Sound insulation and quiet HVAC</li>
                <li>• Mounted whiteboard and printer shelf</li>
              </ul>
            </div>
            <div className="space-y-4">
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Community Space</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Lounge or coffee bar corner</li>
                <li>• Shared meeting area with video conferencing</li>
                <li>• Natural lighting and plants for wellness</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Professional Features Section */}
      <section className="py-16 px-4 bg-secondary/50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-8" style={{ color: '#E4683B' }}>Professional Co-Working Features</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Wifi className="w-8 h-8" style={{ color: '#E4683B' }} />
              </div>
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>High-Speed Internet</h3>
              <p className="text-muted-foreground">Enterprise-grade wifi for multiple users</p>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Monitor className="w-8 h-8" style={{ color: '#E4683B' }} />
              </div>
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Tech Infrastructure</h3>
              <p className="text-muted-foreground">Multiple outlets, charging stations, and displays</p>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Coffee className="w-8 h-8" style={{ color: '#E4683B' }} />
              </div>
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Amenities</h3>
              <p className="text-muted-foreground">Coffee station and comfortable seating areas</p>
            </div>
          </div>
        </div>
      </section>

      {/* Monetization Strategy Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: '#E4683B' }}>Monetization Strategy</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Users className="w-8 h-8" style={{ color: '#E4683B' }} />
              </div>
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Member Rentals</h3>
              <p className="text-muted-foreground">Rent to solopreneurs or startup teams</p>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Calendar className="w-8 h-8" style={{ color: '#E4683B' }} />
              </div>
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Event Hosting</h3>
              <p className="text-muted-foreground">Host productivity retreats or workshops</p>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Building className="w-8 h-8" style={{ color: '#E4683B' }} />
              </div>
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Platform Listings</h3>
              <p className="text-muted-foreground">List on Peerspace or LiquidSpace</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4 bg-secondary/50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold mb-12 text-center" style={{ color: '#E4683B' }}>Why Choose Co-Working ADU?</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <DollarSign className="w-8 h-8" style={{ color: '#E4683B' }} />
              </div>
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Low Overhead</h3>
              <p className="text-muted-foreground">Professional space without downtown rent costs</p>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Users className="w-8 h-8" style={{ color: '#E4683B' }} />
              </div>
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Community Building</h3>
              <p className="text-muted-foreground">Foster local professional relationships</p>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Home className="w-8 h-8" style={{ color: '#E4683B' }} />
              </div>
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Convenient Location</h3>
              <p className="text-muted-foreground">Accessible workspace in residential setting</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>
            Launch Your Backyard Co-Working Space
          </h2>
          <p className="text-xl text-muted-foreground mb-8">
            Create a professional community hub that brings remote workers together in your neighborhood.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {ctaButtons.map((button, index) => (
              <Button
                key={index}
                onClick={button.onClick}
                className="flex items-center gap-2"
                style={{ backgroundColor: index === 0 ? '#E4683B' : 'transparent', borderColor: '#E4683B', color: 'white' }}
                variant={index === 0 ? "default" : "outline"}
              >
                <button.icon className="w-4 h-4" />
                {button.text}
              </Button>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      {/* Calendly Modal */}
      <Dialog open={showCalendly} onOpenChange={setShowCalendly}>
        <DialogContent className="max-w-4xl w-full h-[600px] p-0">
          <div className="w-full h-full">
            <iframe
              src="https://calendly.com/udaadu"
              width="100%"
              height="100%"
              frameBorder="0"
              title="Schedule a consultation"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HowToUseADUAsCoWorkingOrSharedOffice;